import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { addAssetOrItem, setIsLoading } from '@/assetGroupEditor/assetGroup.actions';
import { editAssetGroup, validateItemToAdd } from '@/assetGroupEditor/assetGroup.utilities';
import { IconWithManagedSpinner } from '@/core/IconWithManagedSpinner.atom';
import { sqAssetGroupStore, sqScalingTableDefinitionStore, sqWorksheetStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { useHasAssetGroups } from '@/licenseManagement/licenseManagement.store';
import { CREATED_BY_SEEQ_WORKBENCH } from '@/main/app.constants';
import { setAutoFocusTableDefinitionTab } from '@/main/layout.actions';
import { useLayoutTableDefinitionEditorVisible } from '@/main/layout.store';
import { navigateToTreeBuilderPage } from '@/main/routing.utilities';
import { isItemTable, isScalingTable } from '@/search/search.utilities';
import { canWriteItem } from '@/services/authorization.service';
import { editTableDefinition } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { doTrack } from '@/track/track.service';
import { isAsset, isTableDefinition } from '@/utilities/utilities';
import { isViewOnlyWorkbookMode } from '@/utilities/workbookMode.utilities';
import { setDisplayAssetGroupEditor } from '@/worksheet/worksheet.actions';
import type * as React from 'react';
import type { SelectedSearchItem } from '@/search/search.types';

interface SearchResultAssetIconProps {
  item: SelectedSearchItem & { isAssetGroupItem?: boolean };
  isAssetGroupView: boolean;
}

export const SearchResultAssetIcons = ({ item, isAssetGroupView }: SearchResultAssetIconProps) => {
  const storeAssets = useFluxPath(sqAssetGroupStore, () => sqAssetGroupStore.assets);
  const isTableDefinitionView = useFluxPath(sqWorksheetStore, () => sqWorksheetStore.displayTableDefinitionView);
  const isEditingAssetGroup = useFluxPath(
    sqAssetGroupStore,
    () => isAssetGroupView && sqAssetGroupStore.id === item.id,
  );
  const scalingTableEditorVisible = useLayoutTableDefinitionEditorVisible();
  const isEditingTableDefinition =
    useFluxPath(sqScalingTableDefinitionStore, () => sqScalingTableDefinitionStore.id === item.id) &&
    isTableDefinitionView;
  const hasAssetGroups = useHasAssetGroups();
  const isEditTableDefinitionAllowed = isTableDefinition(item);
  const isEditAllowed =
    isEditTableDefinitionAllowed ||
    (isAsset(item) &&
      hasAssetGroups &&
      canWriteItem(item) &&
      (_.find(item.properties, { name: SeeqNames.Properties.TreeType })?.value === CREATED_BY_SEEQ_WORKBENCH ||
        item.isAssetGroupItem) &&
      _.isEmpty(item.ancestors));

  const disableEdit = isEditingAssetGroup || (isEditingTableDefinition && scalingTableEditorVisible);

  const addAsset = (addMatchingItemsOnly = false, addChildrenOfItemsOfItems = false) => {
    setIsLoading(true);
    const promise = validateItemToAdd({ item: { type: item.type || '' } })
      ? addAssetOrItem({
          assetOrItem: item as SelectedSearchItem & {
            ancestors?: Array<{ type?: string; name?: string; id?: string }>;
          },
          addMatchingItemsOnly,
          addItemsOfItemsChildren: addChildrenOfItemsOfItems,
        })
      : Promise.resolve();

    return promise.finally(() => setIsLoading(false));
  };

  const loadTableDefinitionForEdit = async (event: React.MouseEvent | React.KeyboardEvent): Promise<void> => {
    if (isItemTable(item)) {
      doTrack('Tree Builder', 'Editor opened', { sourceFlow: 'Workbook' });
      navigateToTreeBuilderPage({ tableId: item.id, event });
    } else if (isScalingTable(item)) {
      setAutoFocusTableDefinitionTab(true);
      await editTableDefinition(item, 'SCALING');
    }
  };

  const loadAssetGroupForEdit = (): Promise<void> => {
    setDisplayAssetGroupEditor(true);
    return editAssetGroup(item);
  };

  // Assets always require write permission through isEditAllowed, so their visible action is always edit.
  // Preserve the existing edit fallback when permissions are unavailable; table search results now include them.
  const canEdit =
    isAsset(item) || (!isViewOnlyWorkbookMode() && (item.effectivePermissions ? canWriteItem(item) : !item.isLocked));
  const action = isAsset(item) ? loadAssetGroupForEdit : loadTableDefinitionForEdit;

  const getTooltip = () => {
    if (isAsset(item)) return !isEditingAssetGroup ? 'SEARCH_DATA.EDIT_ASSET_GROUP_TOOLTIP' : undefined;
    if (isScalingTable(item) && !isEditingTableDefinition) {
      return canEdit ? 'SEARCH_DATA.EDIT_SCALING_TABLE_TOOLTIP' : 'SEARCH_DATA.VIEW_SCALING_TABLE_TOOLTIP';
    }
    if (isItemTable(item)) {
      return canEdit ? 'SEARCH_DATA.EDIT_ITEM_TABLE_TOOLTIP' : 'SEARCH_DATA.VIEW_ITEM_TABLE_TOOLTIP';
    }
    return undefined;
  };

  const getTestId = () => {
    if (isAsset(item)) return 'editAssetGroupIcon';
    if (isScalingTable(item)) return canEdit ? 'editScalingTableIcon' : 'viewScalingTableIcon';
    if (isItemTable(item)) return canEdit ? 'editItemTableDefinitionIcon' : 'viewItemTableDefinitionIcon';
    return '';
  };

  return (
    <>
      {isEditAllowed && (
        <IconWithManagedSpinner
          icon={canEdit ? 'fa-pencil' : 'fa-eye'}
          testId={getTestId()}
          disabled={disableEdit}
          large={true}
          extraClassNames="searchBtn"
          action={action}
          actionOnEnter={true}
          tooltip={getTooltip() || ''}
          tooltipDelay={500}
        />
      )}

      {isAssetGroupView && (
        <>
          {/* assets and "items of items'" parents */}
          {(isAsset(item) || (!isAsset(item) && item.hasActiveChildren)) && (
            <IconWithManagedSpinner
              icon="fc-plus-assets"
              extraClassNames="searchBtn"
              action={() => addAsset(false, !isAsset(item))}
              actionOnEnter={true}
              large={true}
              disabled={isEditingAssetGroup}
              testId="addAssetIcon"
              tooltip={!isEditingAssetGroup ? 'SEARCH_DATA.ADD_ASSET_TO_GROUP_TOOLTIP' : ''}
              tooltipDelay={500}
            />
          )}

          {/* assets and "items of items'" parents */}
          {(isAsset(item) || (!isAsset(item) && item.hasActiveChildren)) && !_.isEmpty(storeAssets) && (
            <IconWithManagedSpinner
              icon="fc-plus-selected"
              extraClassNames="searchBtn"
              action={() => addAsset(true)}
              actionOnEnter={true}
              large={true}
              disabled={isEditingAssetGroup}
              testId="addAssetIconSameChildrenOnly"
              tooltip={!isEditingAssetGroup ? 'SEARCH_DATA.ADD_ASSET_TO_GROUP_CHILDREN_MATCH_TOOLTIP' : ''}
              tooltipDelay={500}
            />
          )}

          {/* items (including items of items' parents) */}
          {!isAsset(item) && !isTableDefinition(item) && (
            <IconWithManagedSpinner
              icon="fa-plus-circle"
              extraClassNames="searchBtn"
              action={() => addAsset()}
              actionOnEnter={true}
              large={true}
              testId="addItemToAssetIcon"
              tooltip="SEARCH_DATA.ADD_ASSET_ADD_ITEM_TOOLTIP"
              tooltipDelay={500}
            />
          )}
        </>
      )}
    </>
  );
};
